import React, {useState} from 'react'
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import apiClient from "../../request/http-common";
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CardActions } from '@mui/material';
import { CardContent } from '@mui/material';
import Card from '@mui/material/Card';
import { useMutation } from 'react-query';
import { setFundings } from '../Wallet/walletSlice';
import {
  CardHeader, 
  Divider, 
  Paper, 
  Container, 
  Chip, 
  Avatar, 
  Alert,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Grid from '@mui/material/Grid';
import { styled, alpha } from '@mui/material/styles';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import VerifiedIcon from '@mui/icons-material/Verified';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import GavelIcon from '@mui/icons-material/Gavel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PercentIcon from '@mui/icons-material/Percent';
import PaymentIcon from '@mui/icons-material/Payment';
import LoadingButton from '@mui/lab/LoadingButton';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  marginBottom: theme.spacing(3),
  overflow: 'hidden',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(1.5),
  },
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
  },
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  fontWeight: 600,
  backgroundColor: status === 'high' 
    ? alpha(theme.palette.success.main, 0.1)
    : alpha(theme.palette.warning.main, 0.1),
  color: status === 'high'
    ? theme.palette.success.dark
    : theme.palette.warning.dark,
  '& .MuiChip-icon': {
    color: 'inherit'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.7rem',
    height: 24,
  }
}));

const CardTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.25rem',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '& svg': {
    color: theme.palette.primary.main
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.1rem',
  }
}));

const GradientDivider = styled(Divider)(({ theme }) => ({
  height: '3px',
  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`
}));

const AnimatedButton = styled(LoadingButton)(({ theme }) => ({
  fontWeight: 600,
  borderRadius: '8px',
  padding: '10px 24px',
  textTransform: 'none',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
  },
}));

const CompactPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  }
}));

const ResponsiveBox = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
  }
}));

const ResponsiveAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  width: 56,
  height: 56,
  [theme.breakpoints.down('sm')]: {
    width: 42,
    height: 42,
  }
}));

const Seller = () => {
  const [error, setErrors] = useState('');
  const formatPrice = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', currencySign: 'standard' }).format(amount);
  const navigate = useNavigate();
  const trader = useSelector((state) => state.coins.p2pTrader);
  const [coin, setCoin] = useState('');
  const dispatch = useDispatch();
  const [amount, setAmount] = useState(null);
  const [bank, setBank] = useState(null);
  const [details, setDetails] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { isLoading: isSendingRequest, mutate: postRequest } = useMutation(
    async () => {
      return await apiClient.post(`/api/save-funding`, {
        amount: amount,
        coin: trader.id,
        wallet_name: bank,
        wallet_address: details
      });
    },
    {
      onSuccess: (res) => {
        dispatch(setFundings(res.data.fundings))
        setCoin('');
        localStorage.setItem('notification', JSON.stringify({message : `${res.data.message}`, 'type': 'success' }));
        const event = new Event('newMessage');
        window.dispatchEvent(event);
        const event2 = new Event('processed');
        window.dispatchEvent(event2);
        setTimeout(() => {
          navigate(`/payment/${res.data.funding.id}`);
        }, 2000);
      },
      onError: (err) => {
        if (err.response?.data.length) {
          let myerror = err.response?.data || err;
          const event2 = new Event('processed');
          window.dispatchEvent(event2);
        } else {
          let errMessage = err.response?.data.message || err;
          localStorage.setItem('notification', JSON.stringify({message : `${errMessage}`, 'type': 'error' }));
          const event = new Event('newMessage');
          window.dispatchEvent(event);
          const event2 = new Event('processed');
          window.dispatchEvent(event2);
        }
      },
    }
  );

  const completionStatus = parseInt(trader.percentage) > 90 ? 'high' : 'medium';

  return (
    <Container maxWidth="lg" disableGutters={isMobile}>
      <ResponsiveBox 
        sx={{ 
          mt: { xs: 4, sm: 7 },
          mb: { xs: 2, sm: 5 }
        }}
      >
        <StyledCard elevation={0}>
          <CardHeader
            sx={{ 
              px: { xs: 2, sm: 3 },
              py: { xs: 1.5, sm: 2 }
            }}
            avatar={
              <ResponsiveAvatar>
                <AccountCircleIcon fontSize={isMobile ? "medium" : "large"} />
              </ResponsiveAvatar>
            }
            title={
              <CardTitle variant="h5" component="div">
                <span>Trader Information</span>
                <Tooltip title="Verified trader" placement="right">
                  <VerifiedIcon fontSize={isMobile ? "small" : "small"} color="primary" />
                </Tooltip>
              </CardTitle>
            }
          />
          <GradientDivider />
          <List sx={{ width: '100%', bgcolor: 'transparent', p: 0 }}>
            <StyledListItem alignItems="flex-start">
              <ListItemText
                primary={
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: { xs: 'flex-start', sm: 'center' }, 
                    flexDirection: { xs: 'column', sm: 'row' },
                    mb: 1,
                    gap: { xs: 1, sm: 0 }
                  }}>
                    <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 700, mr: 2 }}>
                      {trader.trader_name}
                    </Typography>
                    <StatusChip 
                      status={completionStatus}
                      icon={<PercentIcon fontSize={isMobile ? "small" : "medium"} />} 
                      label={`${parseInt(trader.percentage)}% Completion`} 
                      size="small" 
                    />
                  </Box>
                }
                secondary={
                  <Box sx={{ mt: { xs: 1, sm: 2 } }}>
                    <Grid container spacing={{ xs: 1.5, sm: 3 }}>
                      <Grid item xs={6} sm={6}>
                        <CompactPaper elevation={0} sx={{ bgcolor: (theme) => alpha(theme.palette.background.default, 0.5) }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                            Available Quantity
                          </Typography>
                          <Typography variant={isMobile ? "body1" : "h6"} sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {formatPrice(trader.quantity)}
                          </Typography>
                        </CompactPaper>
                      </Grid>
                      <Grid item xs={6} sm={6}>
                        <CompactPaper elevation={0} sx={{ bgcolor: (theme) => alpha(theme.palette.background.default, 0.5) }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                            Orders Completed
                          </Typography>
                          <Typography variant={isMobile ? "body1" : "h6"} sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {trader.orders}
                          </Typography>
                        </CompactPaper>
                      </Grid>
                      <Grid item xs={12}>
                        <CompactPaper elevation={0} sx={{ bgcolor: (theme) => alpha(theme.palette.background.default, 0.5) }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                            Limits
                          </Typography>
                          <Typography variant={isMobile ? "body1" : "h6"} sx={{ fontWeight: 600 }}>
                            {formatPrice(trader.min)} - {formatPrice(trader.max)}
                          </Typography>
                        </CompactPaper>
                      </Grid>
                      <Grid item xs={12}>
                        <Alert 
                          severity="info" 
                          sx={{ 
                            borderRadius: { xs: theme.spacing(1), sm: theme.spacing(2) },
                            py: { xs: 1, sm: 1.5 },
                            px: { xs: 1.5, sm: 2 },
                            '& .MuiAlert-message': {
                              fontSize: { xs: '0.8rem', sm: '0.875rem' }
                            }
                          }}
                        >
                          {trader.payment_description}
                        </Alert>
                      </Grid>
                    </Grid>
                  </Box>
                }
              />
            </StyledListItem>
          </List>
        </StyledCard>

        <StyledCard elevation={0}>
          <CardHeader
            sx={{ 
              px: { xs: 2, sm: 3 },
              py: { xs: 1.5, sm: 2 }
            }}
            avatar={
              <ResponsiveAvatar>
                <PaymentIcon fontSize={isMobile ? "medium" : "large"} />
              </ResponsiveAvatar>
            }
            title={
              <CardTitle variant="h5" component="div">
                Payment Information
              </CardTitle>
            }
          />
          <GradientDivider />
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel htmlFor="amount" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>Amount (USDT)</InputLabel>
                  <OutlinedInput
                    onChange={(e) => setAmount(e.target.value)}
                    id="amount"
                    type="number"
                    name="amount"
                    label="Amount (USDT)"
                    error={error !== '' && error.amount}
                    sx={{ 
                      height: { xs: 48, sm: 56 }, 
                      borderRadius: theme.spacing(1),
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderWidth: '2px',
                        },
                      },
                    }}
                  />
                  {error !== '' && error.amount && (
                    <Typography color="error" variant="caption" sx={{ mt: 1, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                      {error.amount}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel htmlFor="bank" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>Bank Name</InputLabel>
                  <OutlinedInput
                    onChange={(e) => setBank(e.target.value)}
                    id="bank"
                    type="text"
                    name="bank"
                    label="Bank Name"
                    error={error !== '' && error.bank}
                    sx={{ 
                      height: { xs: 48, sm: 56 }, 
                      borderRadius: theme.spacing(1),
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderWidth: '2px',
                        },
                      },
                    }}
                  />
                  {error !== '' && error.bank && (
                    <Typography color="error" variant="caption" sx={{ mt: 1, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                      {error.bank}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel htmlFor="details" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>Account Details</InputLabel>
                  <OutlinedInput
                    onChange={(e) => setDetails(e.target.value)}
                    id="details"
                    type="text"
                    name="details"
                    multiline
                    rows={3}
                    label="Account Details"
                    error={error !== '' && error.details}
                    sx={{ 
                      borderRadius: theme.spacing(1),
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderWidth: '2px',
                        },
                      },
                    }}
                  />
                  {error !== '' && error.details && (
                    <Typography color="error" variant="caption" sx={{ mt: 1, fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                      {error.details}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            </Grid>
            
            <Box sx={{ 
              p: { xs: 1.5, sm: 2 }, 
              borderRadius: theme.spacing(1), 
              bgcolor: (theme) => alpha(theme.palette.warning.light, 0.1),
              border: '1px dashed',
              borderColor: 'warning.main',
              mb: { xs: 1.5, sm: 2 },
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1
            }}>
              <InfoOutlinedIcon color="warning" sx={{ mt: 0.2, fontSize: { xs: '1rem', sm: '1.25rem' } }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Please ensure the amount is within the accepted limits: {formatPrice(trader.min)} - {formatPrice(trader.max)}
              </Typography>
            </Box>
          </CardContent>
          <CardActions sx={{ 
            p: { xs: 2, sm: 3 }, 
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, sm: 0 }
          }}>
            <Button
              onClick={() => setCoin('')}
              variant="outlined"
              color="error"
              fullWidth={isMobile}
              sx={{ 
                minWidth: { xs: '100%', sm: 120 }, 
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                order: { xs: 2, sm: 1 }
              }}
            >
              Cancel
            </Button>
            <AnimatedButton
              onClick={postRequest}
              variant="contained"
              color="primary"
              loading={isSendingRequest}
              loadingPosition="center"
              fullWidth={isMobile}
              sx={{
                minWidth: { xs: '100%', sm: 150 },
                order: { xs: 1, sm: 2 }
              }}
            >
              {isSendingRequest ? 'Processing...' : 'Submit Order'}
            </AnimatedButton>
          </CardActions>
        </StyledCard>

        <StyledCard elevation={0}>
          <CardHeader
            sx={{ 
              px: { xs: 2, sm: 3 },
              py: { xs: 1.5, sm: 2 }
            }}
            avatar={
              <ResponsiveAvatar>
                <GavelIcon fontSize={isMobile ? "medium" : "large"} />
              </ResponsiveAvatar>
            }
            title={
              <CardTitle variant="h5" component="div">
                Advertiser Terms
              </CardTitle>
            }
          />
          <GradientDivider />
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: { xs: 2, sm: 3 }, 
                borderRadius: theme.spacing(1), 
                backgroundColor: (theme) => alpha(theme.palette.background.default, 0.7),
                border: '1px solid',
                borderColor: (theme) => alpha(theme.palette.divider, 0.1),
              }}
            >
              <Typography 
                variant="body1" 
                sx={{ 
                  whiteSpace: 'pre-line',
                  lineHeight: 1.7,
                  color: 'text.primary',
                  fontSize: { xs: '0.85rem', sm: '1rem' } 
                }}
              >
                {trader.terms}
              </Typography>
            </Paper>
          </CardContent>
        </StyledCard>
      </ResponsiveBox>
    </Container>
  );
};

export default Seller;
